import * as React from "react";
import { styled } from "@mui/material";
import { Avatar, Message } from "@chatscope/chat-ui-kit-react";
import { USER_AVATAR_SRC } from "../styles/Theme";
import {
  LineActions,
  LineActionsButton,
} from "fe-web-components/layout/buttons/LineActions";
import { ReactComponent as PencilIcon } from "../assets/pencilIcon.svg";
import { ReactComponent as CheckIcon } from "fe-web-components/assets/checkIcon.svg";
import CustomTextField from "./UI/CustomTextField";
import { useCallback } from "react";
import { ChatMessage } from "../context/sessionStore";
import useSessionStore from "../context/useSessionStore";
import { useEventReportedContext } from "../analytics/EventReporterContext";
import { MIX_PANEL_EVENTS } from "../analytics/eventReporter";
import { isRtl } from "../utils";
import { useTranslateByGender } from "fe-common/locale/useTranslateByGender";

//region [[ Styles ]]

const QuestionView = styled("div")(({ historic }: { historic: boolean }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: 12,
  width: "100%",
  marginTop: historic ? 0 : -10,
}));

//endregion [[ Styles ]]

//region [[ Props ]]

export interface QuestionProps {
  msg: ChatMessage;
  historic?: boolean;
}

//endregion [[ Props ]]

//region [[ Functions ]]
//endregion [[ Functions ]]

export const Question = ({ historic, msg }: QuestionProps) => {
  const { index, text } = msg;
  const [editMode, setEditMode] = React.useState(false);
  const [qText, setQText] = React.useState(text);
  const { replaceQuestion, ragSettings } = useSessionStore();
  const reporter = useEventReportedContext();
  const rtlDisplay = isRtl(text, ragSettings?.rtlDisplay);
  const { translate } = useTranslateByGender();

  const handleUpdate = useCallback(() => {
    if (qText !== text || ragSettings.debugMode) {
      reporter?.reportEvent(MIX_PANEL_EVENTS.QUESTION_EDITED);
      replaceQuestion(
        index,
        qText,
        null,
        (answer) => reporter?.reportAnswer(answer),
      );
    }
    setEditMode(false);
  }, [index, qText, text, replaceQuestion, reporter, ragSettings]);

  return (
    <Message
      model={{
        payload: (
          <Message.CustomContent>
            <QuestionView dir={rtlDisplay ? "rtl" : ""} historic={historic}>
              {editMode ? (
                <CustomTextField
                  value={qText}
                  fullWidth
                  multiline
                  onChange={(e) => setQText(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Escape") {
                      setQText(text);
                      setEditMode(false);
                    } else if (e.key === "Enter" && !e.shiftKey) {
                      handleUpdate();
                    }
                  }}
                />
              ) : (
                text
              )}
              {!historic && (
                <LineActions className={editMode ? "active" : ""}>
                  <LineActionsButton
                    onClick={() =>
                      editMode ? handleUpdate() : setEditMode(true)
                    }
                    title={translate("EDIT")}
                  >
                    {editMode ? <CheckIcon /> : <PencilIcon />}
                  </LineActionsButton>
                </LineActions>
              )}
            </QuestionView>
          </Message.CustomContent>
        ),
        sentTime: "just now",
        sender: "User",
        direction: "incoming",
        position: "normal",
      }}
      avatarPosition={rtlDisplay ? "tr" : "tl"}
    >
      <Avatar src={USER_AVATAR_SRC} name="User" size={historic ? "sm" : "md"} />
    </Message>
  );
};
